/**
 * Constantes del Clinical Dashboard
 * Paleta de colores clínicos, categorías de tareas, prioridades, statuses
 */

// ─── Health Status Colors ────────────────────────────────────────
export const HEALTH_STATUS_COLORS = {
  Well:        { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0" },
  Acute:       { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  Chronic:     { bg: "#DBEAFE", text: "#1E40AF", border: "#BFDBFE" },
  "At Risk":   { bg: "#FFEDD5", text: "#9A3412", border: "#FED7AA" },
  Complex:     { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
  "End of Life": { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  "No Status": { bg: "#F1F5F9", text: "#475569", border: "#E2E8F0" },
};

// ─── Visit Timeframe Colors ─────────────────────────────────────
export const VISIT_COLORS = {
  "12+ Months": { bg: "#FEE2E2", text: "#991B1B" },
  "9 Months":   { bg: "#FFEDD5", text: "#9A3412" },
  "6 Months":   { bg: "#FEF3C7", text: "#92400E" },
  "3 Months":   { bg: "#DCFCE7", text: "#166534" },
  "<3 Months":  { bg: "#D1FAE5", text: "#065F46" },
};

// ─── LACE Score Ranges ──────────────────────────────────────────
export const LACE_RANGES = {
  low:      { min: 0,  max: 9,  color: "#16A34A", label: "Low Risk" },
  moderate: { min: 10, max: 15, color: "#D97706", label: "Moderate" },
  high:     { min: 16, max: 99, color: "#DC2626", label: "High Risk" },
};

export const getLaceColor = (score) => {
  if (score == null) return { color: "#94A3B8", label: "N/A" };
  if (score <= 9) return LACE_RANGES.low;
  if (score <= 15) return LACE_RANGES.moderate;
  return LACE_RANGES.high;
};

// ─── Alert Type Colors (border-left) ────────────────────────────
export const ALERT_COLORS = {
  admitted:   "#DC2626",
  discharged: "#D97706",
  emergency:  "#EA580C",
};

// ─── Compliance Thresholds ──────────────────────────────────────
export const getComplianceColor = (rate) => {
  if (rate < 30) return "#DC2626";
  if (rate < 60) return "#D97706";
  if (rate < 85) return "#2563EB";
  return "#16A34A";
};

// ─── KPI Segment Definitions ────────────────────────────────────
export const KPI_SEGMENTS = [
  { key: "assignedPatients", label: "Assigned",   icon: "GroupIcon",          color: "#2563EB", bgColor: "#EFF6FF" },
  { key: "highCost",         label: "High Cost",  icon: "AttachMoneyIcon",    color: "#92400E", bgColor: "#FEF3C7" },
  { key: "highRisk",         label: "High Risk",  icon: "WarningAmberIcon",   color: "#9A3412", bgColor: "#FFEDD5" },
  { key: "emergency",        label: "Emergency",  icon: "LocalHospitalIcon",  color: "#991B1B", bgColor: "#FEE2E2" },
  { key: "discharged",       label: "Discharged", icon: "ExitToAppIcon",      color: "#1E40AF", bgColor: "#DBEAFE" },
  { key: "admitted",         label: "Admitted",    icon: "HotelIcon",          color: "#991B1B", bgColor: "#FEE2E2" },
];

// ─── Task Categories (IDs = control.TAB_STATUSES grupo task_category) ──
export const TASK_CATEGORIES = [
  { id: 33, key: "follow_up",        label: "Follow-up Visit" },
  { id: 34, key: "care_gap",         label: "Care Gap Review" },
  { id: 35, key: "coding_review",    label: "Coding Review" },
  { id: 36, key: "outreach_call",    label: "Outreach Call" },
  { id: 37, key: "lab_review",       label: "Lab Review" },
  { id: 38, key: "referral",         label: "Referral" },
  { id: 39, key: "documentation",    label: "Documentation" },
  { id: 40, key: "patient_recall",   label: "Patient Recall" },
  { id: 41, key: "other",            label: "Other" },
];

// ─── Task Priorities (IDs = control.TAB_STATUSES grupo task_priority) ──
export const TASK_PRIORITIES = [
  { id: 30, key: "high",   label: "High",   color: "#DC2626" },
  { id: 31, key: "medium", label: "Medium", color: "#D97706" },
  { id: 32, key: "low",    label: "Low",    color: "#16A34A" },
];

// ─── Task Statuses (IDs = control.TAB_STATUSES grupo task_status) ──
export const TASK_STATUSES = [
  { id: 26, key: "pending",     label: "Pending" },
  { id: 27, key: "in_progress", label: "In Progress" },
  { id: 28, key: "completed",   label: "Completed" },
  { id: 29, key: "cancelled",   label: "Cancelled" },
];

// ─── Health Status Severity Weight (para triage sort) ───────────
export const HEALTH_SEVERITY_WEIGHT = {
  "End of Life": 6,
  Complex: 5,
  "At Risk": 4,
  Chronic: 3,
  Acute: 2,
  Well: 1,
  "No Status": 0,
};

// ─── Visit Recency Weight (para triage sort) ────────────────────
export const VISIT_RECENCY_WEIGHT = {
  "12+ Months": 5,
  "9 Months": 4,
  "6 Months": 3,
  "3 Months": 2,
  "<3 Months": 1,
};

// ─── SantePlus Visit Colors (productividad) ─────────────────────
export const SP_VISIT_COLORS = {
  recent:   { bg: "#DCFCE7", text: "#166534" },  // < 3 meses
  moderate: { bg: "#FEF3C7", text: "#92400E" },  // 3-6 meses
  old:      { bg: "#FEE2E2", text: "#991B1B" },  // > 6 meses
  none:     { bg: "#F1F5F9", text: "#94A3B8" },  // null
};

/**
 * Determina la categoría de antigüedad de una visita SP.
 * @param {string|null} dateStr - Fecha en formato YYYY-MM-DD
 * @returns {"recent"|"moderate"|"old"|"none"}
 */
export const getSpVisitAge = (dateStr) => {
  if (!dateStr) return "none";
  const now = new Date();
  const visit = new Date(dateStr);
  const diffMs = now - visit;
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  if (diffMonths < 3) return "recent";
  if (diffMonths <= 6) return "moderate";
  return "old";
};
